import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Passwordless from 'supertokens-auth-react/recipe/passwordless';
import Session from 'supertokens-auth-react/recipe/session';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Icon,
  NewButton,
} from '@eluve/components';
import { formatErrorForLogging } from '@eluve/eluve-errors';
import { useNamedLogger } from '@eluve/logger';

export const MagicLinkVerifyPage = () => {
  const logger = useNamedLogger('MagicLinkVerify');
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const [magicLinkFailed, setMagicLinkFailed] = useState(false);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const verifyMagicLink = async () => {
      logger.info('MagicLinkVerifyPage loaded, starting verification flow');

      try {
        logger.info('Signing out of current session');
        await Session.signOut();
        logger.info('Successfully signed out of current session');
      } catch (error) {
        logger.error('Error signing out of the current session', {
          error: formatErrorForLogging(error),
        });
        setMagicLinkFailed(true);
        return;
      }

      try {
        logger.info('Consuming magic link code');
        const response = await Passwordless.consumeCode();
        logger.info('Magic link code consumption response received', {
          status: response.status,
        });

        if (response.status === 'OK') {
          logger.info('Magic link code consumed successfully');

          const params = new URLSearchParams(location.search);
          let redirectTo = params.get('redirectTo') ?? '/';

          if (!redirectTo.startsWith('/')) {
            redirectTo = `/${redirectTo}`;
          }

          logger.info('Extracted redirect target', { redirectTo });

          // Refresh claims to capture tenant auto-approval
          try {
            logger.info('Refreshing allowed tenants');
            await apiClient.auth.refreshAllowedTenants({ body: {} });
            logger.info('Successfully refreshed allowed tenants');
          } catch (error) {
            logger.warn(
              'Failed to refresh allowed tenants after magic link verification',
              { error: formatErrorForLogging(error) },
            );
          }

          logger.info('Login successful, redirecting to:', { redirectTo });
          navigate(redirectTo);
        } else {
          logger.info('Magic link verification was not successful', {
            response,
          });
          setMagicLinkFailed(true);
        }
      } catch (error) {
        logger.error('Error consuming magic link', {
          error: formatErrorForLogging(error),
        });
        setMagicLinkFailed(true);
      }
    };

    verifyMagicLink();
  }, [navigate, logger, location, apiClient]);

  // Auto-redirect to login page after 5 seconds when there's an error
  useEffect(() => {
    if (magicLinkFailed) {
      logger.info('Magic link failed, starting auto-redirect countdown (5s)');
      const timer = setTimeout(() => {
        logger.info('Auto-redirecting to login page');
        navigate('/login');
      }, 5000);

      // Countdown timer
      const countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownTimer);
      };
    }
  }, [magicLinkFailed, navigate]);

  const handleGoToLogin = () => {
    logger.info('User manually clicked "Go to Login" button');
    navigate('/login');
  };

  if (magicLinkFailed) {
    logger.info('Rendering error UI');
    return (
      <div className="grid h-full place-items-center p-4">
        <div className="max-w-md space-y-4">
          <Alert variant="destructive">
            <Icon name="AlertCircle" className="size-4" />
            <AlertTitle>Login Failed</AlertTitle>
            <AlertDescription>
              Unable to verify your login link. Please try again.
            </AlertDescription>
          </Alert>
          <div className="space-y-2 text-center">
            <NewButton
              text="Go to Login"
              type="primary"
              size="m"
              onClick={handleGoToLogin}
            />
            <p className="text-sm text-text-tertiary">
              You will be automatically redirected to the login page in{' '}
              {countdown}
              {countdown === 1 ? ' second' : ' seconds'}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  logger.info('Rendering loading UI');
  return (
    <div className="grid h-full place-items-center">
      <Icon
        className="animate-spin text-border-accent-quaternary"
        name="Loader2Icon"
        size="lg"
      />
    </div>
  );
};
